import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { champFields } from '../utilities/tournamentsApi';
import { getFlagUrl } from '../utilities/flags';
import { formatDayMonth, formatDayMonthTime } from '../utilities/schedulerApi';
import { scoreText } from '../utilities/matchesApi';
import {
  GENDER_OPTIONS,
  STATUS_OPTIONS,
  champDatesPayload,
  datesAreValid,
  datesPayload,
  defaultDateRange,
  extractBirthdays,
  extractPlayersCount,
  extractPlayersList,
  extractStats,
  parseAgeInput,
  personName,
  teamNames,
  todayIso,
} from '../utilities/reportsApi';
import { playerCardPath } from '../utilities/playersApi';
import { PlayerThumb, ReportBlock, ReportDateRange, ReportPage, ReportSearch, TeamThumb } from '../components/ReportUi';
import '../styles/stadiumsManagement.css';
import '../styles/reports.css';

const initialRange = defaultDateRange();
const today = todayIso();

function formatInsuranceDate(isoDate) {
  const match = String(isoDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  return `${match[3]}.${match[2]}.${match[1]}`;
}

function insuranceMeta(player, t) {
  if (!player?.isInsured) return t('rpNotInsured');
  const until = formatInsuranceDate(player.insuranceUntil);
  return until
    ? `${t('rpInsured')} · ${t('rpInsuranceUntil', { date: until })}`
    : t('rpInsured');
}

function RankRow({ rank, thumb, title, meta, count, to }) {
  const content = (
    <>
      <span className="rp-row-count">{rank}</span>
      {thumb}
      <div className="rp-row-main">
        <span className="rp-row-title">{title}</span>
        {meta ? <span className="rp-row-meta">{meta}</span> : null}
      </div>
      <span className="rp-row-count">{count}</span>
    </>
  );

  if (to) {
    return (
      <Link className="rp-row rp-row-link" to={to}>
        {content}
      </Link>
    );
  }

  return <div className="rp-row">{content}</div>;
}

function MatchMini({ match }) {
  const home = match?.home_team || {};
  const guest = match?.guest_team || {};
  return (
    <div className="rp-match">
      <Link className="rp-match-link" to={`/manage_match/${match.id}`}>
        <TeamThumb team={home} />
        <span className="rp-match-name">{home.short_name || home.name || '—'}</span>
        <span className="rp-match-score">{scoreText(match)}</span>
        <span className="rp-match-name is-guest">{guest.short_name || guest.name || '—'}</span>
        <TeamThumb team={guest} />
      </Link>
      <span className="rp-row-meta">
        {formatDayMonthTime(match.date, match.time) || formatDayMonth(match.date)}
        {match.league_name ? ` · ${match.league_name}` : ''}
      </span>
    </div>
  );
}

function PlayerStats() {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();

  const [gender, setGender] = useState('all');
  const [status, setStatus] = useState('all');
  const [ageFrom, setAgeFrom] = useState('');
  const [ageTo, setAgeTo] = useState('');
  const [ageQuery, setAgeQuery] = useState({ from: '', to: '' });
  const [citySearch, setCitySearch] = useState('');
  const [cityQuery, setCityQuery] = useState('');
  const [playersCount, setPlayersCount] = useState({ players_count: 0, cities: [] });
  const [countLoading, setCountLoading] = useState(false);
  const [countError, setCountError] = useState('');
  const [expandedChampId, setExpandedChampId] = useState(null);
  const [playersByChamp, setPlayersByChamp] = useState({});
  const [playersLoadingId, setPlayersLoadingId] = useState(null);
  const [playersErrorByChamp, setPlayersErrorByChamp] = useState({});

  const [birthdayFrom, setBirthdayFrom] = useState(today);
  const [birthdayTo, setBirthdayTo] = useState(today);
  const [birthdays, setBirthdays] = useState([]);
  const [birthdaysLoading, setBirthdaysLoading] = useState(false);
  const [birthdaysError, setBirthdaysError] = useState('');

  const [statsDates, setStatsDates] = useState(initialRange);
  const [stats, setStats] = useState(extractStats({}));
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState('');
  const [mobileTab, setMobileTab] = useState('count');

  const mobileTabs = [
    { id: 'count', label: t('rpTabPlayersCount') },
    { id: 'birthdays', label: t('rpTabBirthdays') },
    { id: 'best', label: t('rpTabBest') },
  ];

  useEffect(() => {
    const timer = setTimeout(() => setCityQuery(citySearch.trim()), 350);
    return () => clearTimeout(timer);
  }, [citySearch]);

  useEffect(() => {
    const timer = setTimeout(() => setAgeQuery({ from: ageFrom.trim(), to: ageTo.trim() }), 350);
    return () => clearTimeout(timer);
  }, [ageFrom, ageTo]);

  useEffect(() => {
    let cancelled = false;
    const fromAge = parseAgeInput(ageQuery.from);
    const toAge = parseAgeInput(ageQuery.to);
    if (fromAge === false || toAge === false || (fromAge != null && toAge != null && fromAge > toAge)) {
      setCountError(t('rpAgeInvalid'));
      setCountLoading(false);
      return undefined;
    }
    setCountLoading(true);
    setCountError('');
    setExpandedChampId(null);
    setPlayersByChamp({});
    setPlayersErrorByChamp({});
    setPlayersLoadingId(null);
    const body = {
      gender,
      status,
      search: cityQuery,
    };
    if (fromAge != null) body.age_from = fromAge;
    if (toAge != null) body.age_to = toAge;
    authorizedPost(dispatch, API_URLS.getAdminReportPlayersCount, body)
      .then((data) => {
        if (!cancelled) setPlayersCount(extractPlayersCount(data));
      })
      .catch((error) => {
        if (!cancelled) setCountError(getApiErrorMessage(error, t('rpNoData')));
      })
      .finally(() => {
        if (!cancelled) setCountLoading(false);
      });
    return () => { cancelled = true; };
  }, [ageQuery.from, ageQuery.to, cityQuery, dispatch, gender, status, t]);

  const loadChampPlayers = (champId) => {
    if (!champId) return;
    if (playersByChamp[champId]) return;
    const fromAge = parseAgeInput(ageQuery.from);
    const toAge = parseAgeInput(ageQuery.to);
    if (fromAge === false || toAge === false || (fromAge != null && toAge != null && fromAge > toAge)) {
      return;
    }
    setPlayersLoadingId(champId);
    setPlayersErrorByChamp((prev) => {
      if (!prev[champId]) return prev;
      const next = { ...prev };
      delete next[champId];
      return next;
    });
    const body = {
      ...champFields(champId),
      gender,
      status,
    };
    if (fromAge != null) body.age_from = fromAge;
    if (toAge != null) body.age_to = toAge;
    authorizedPost(dispatch, API_URLS.getAdminReportPlayersList, body)
      .then((data) => {
        setPlayersByChamp((prev) => ({ ...prev, [champId]: extractPlayersList(data) }));
      })
      .catch((error) => {
        setPlayersErrorByChamp((prev) => ({
          ...prev,
          [champId]: getApiErrorMessage(error, t('rpNoData')),
        }));
      })
      .finally(() => {
        setPlayersLoadingId((current) => (current === champId ? null : current));
      });
  };

  const toggleChampPlayers = (champId) => {
    if (!champId) return;
    if (expandedChampId === champId) {
      setExpandedChampId(null);
      return;
    }
    setExpandedChampId(champId);
    loadChampPlayers(champId);
  };

  useEffect(() => {
    if (!champId) return undefined;
    const from = birthdayFrom || today;
    const to = birthdayTo || from;
    if (!datesAreValid(from, to)) {
      setBirthdaysError(t('rpNeedDates'));
      return undefined;
    }
    let cancelled = false;
    setBirthdaysLoading(true);
    setBirthdaysError('');
    const body = {
      ...champFields(champId),
      ...(from === to ? { date: from } : datesPayload(from, to)),
    };
    authorizedPost(dispatch, API_URLS.getAdminReportBirthdays, body)
      .then((data) => {
        if (!cancelled) setBirthdays(extractBirthdays(data));
      })
      .catch((error) => {
        if (!cancelled) setBirthdaysError(getApiErrorMessage(error, t('rpNoData')));
      })
      .finally(() => {
        if (!cancelled) setBirthdaysLoading(false);
      });
    return () => { cancelled = true; };
  }, [birthdayFrom, birthdayTo, champId, dispatch, t]);

  useEffect(() => {
    if (!champId) return undefined;
    if (!datesAreValid(statsDates.from, statsDates.to)) {
      setStatsError(t('rpNeedDates'));
      return undefined;
    }
    let cancelled = false;
    setStatsLoading(true);
    setStatsError('');
    authorizedPost(dispatch, API_URLS.getAdminReportStats, champDatesPayload(champId, statsDates.from, statsDates.to))
      .then((data) => {
        if (!cancelled) setStats(extractStats(data));
      })
      .catch((error) => {
        if (!cancelled) setStatsError(getApiErrorMessage(error, t('rpNoData')));
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => { cancelled = true; };
  }, [champId, dispatch, statsDates.from, statsDates.to, t]);

  return (
    <ReportPage
      title={t('navPlayerStats')}
      message={!champId && !champsLoading ? t('rpNeedChamp') : ''}
      tabs={mobileTabs}
      activeTab={mobileTab}
      onTabChange={setMobileTab}
    >
      <ReportBlock
        tabId="count"
        tabActive={mobileTab === 'count'}
        title={t('rpPlayersCount')}
        loading={countLoading}
        error={countError}
        filters={(
          <>
            <label className="st-field">
              <span>{t('rpGender')}</span>
              <select className="st-select" value={gender} onChange={(event) => setGender(event.target.value)}>
                {GENDER_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>{t(item.labelKey)}</option>
                ))}
              </select>
            </label>
            <label className="st-field">
              <span>{t('rpStatus')}</span>
              <select className="st-select" value={status} onChange={(event) => setStatus(event.target.value)}>
                {STATUS_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>{t(item.labelKey)}</option>
                ))}
              </select>
            </label>
            <label className="st-field">
              <span>{t('rpAge')}</span>
              <div className="rp-dates">
                <input
                  type="number"
                  min="0"
                  max="120"
                  inputMode="numeric"
                  className="st-input"
                  value={ageFrom}
                  placeholder={t('rpAgeFrom')}
                  onChange={(event) => setAgeFrom(event.target.value)}
                />
                <input
                  type="number"
                  min="0"
                  max="120"
                  inputMode="numeric"
                  className="st-input"
                  value={ageTo}
                  placeholder={t('rpAgeTo')}
                  onChange={(event) => setAgeTo(event.target.value)}
                />
              </div>
            </label>
            <ReportSearch
              value={citySearch}
              onChange={setCitySearch}
              placeholder={t('rpSearchCity')}
            />
          </>
        )}
      >
        <div className="rp-stat">{t('rpPlayersTotal', { count: playersCount.players_count })}</div>
        {playersCount.cities.length ? playersCount.cities.map((city) => {
          const flagUrl = getFlagUrl(city.city_flag);
          return (
            <div className="rp-group" key={city.city_id || city.city_name}>
              <div className="rp-group-title">
                {flagUrl ? <img className="rp-flag" src={flagUrl} alt="" /> : null}
                <span>{city.city_name || t('rpUnknown')}</span>
                <span className="rp-group-count">{city.players_count || 0}</span>
              </div>
              {(city.championships || []).map((champ) => {
                const champId = champ.id;
                const isOpen = expandedChampId === champId;
                const list = playersByChamp[champId];
                const listError = playersErrorByChamp[champId];
                const listLoading = playersLoadingId === champId;
                return (
                  <div className="rp-champ" key={champId}>
                    <button
                      type="button"
                      className={`rp-sub rp-sub-btn${isOpen ? ' is-open' : ''}`}
                      onClick={() => toggleChampPlayers(champId)}
                      aria-expanded={isOpen}
                      title={isOpen ? t('rpHidePlayers') : t('rpShowPlayers')}
                    >
                      <span className="rp-sub-label">
                        <span className="rp-sub-chevron" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
                        {champ.name}
                      </span>
                      <span className="rp-sub-count">{champ.players_count || 0}</span>
                    </button>
                    {isOpen ? (
                      <div className="rp-champ-players">
                        {listLoading ? <div className="rp-empty">{t('rpPlayersLoading')}</div> : null}
                        {!listLoading && listError ? <div className="rp-empty">{listError}</div> : null}
                        {!listLoading && !listError && list && !list.length ? (
                          <div className="rp-empty">{t('rpNoData')}</div>
                        ) : null}
                        {!listLoading && !listError && list && list.length ? list.map((player) => (
                          <Link
                            key={player.id}
                            className="rp-row rp-row-link rp-player-row"
                            to={playerCardPath(player.id)}
                          >
                            <PlayerThumb player={player} />
                            <div className="rp-row-main">
                              <span className="rp-row-title">{personName(player) || t('rpUnknown')}</span>
                              {teamNames(player.teams) ? (
                                <span className="rp-row-meta">{teamNames(player.teams)}</span>
                              ) : null}
                            </div>
                            <span className={`rp-insure${player.isInsured ? ' is-yes' : ' is-no'}`}>
                              {insuranceMeta(player, t)}
                            </span>
                          </Link>
                        )) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          );
        }) : <div className="rp-empty">{t('rpNoData')}</div>}
      </ReportBlock>

      <ReportBlock
        tabId="birthdays"
        tabActive={mobileTab === 'birthdays'}
        title={t('rpBirthdays')}
        loading={birthdaysLoading}
        error={birthdaysError}
        filters={(
          <ReportDateRange
            from={birthdayFrom}
            to={birthdayTo}
            onFrom={setBirthdayFrom}
            onTo={setBirthdayTo}
            t={t}
            disabled={!champId}
          />
        )}
      >
        {birthdays.length ? birthdays.map((player) => (
          <Link
            key={player.id}
            className="rp-row rp-row-link"
            to={playerCardPath(player.id)}
          >
            <PlayerThumb player={player} />
            <div className="rp-row-main">
              <span className="rp-row-title">{personName(player) || t('rpUnknown')}</span>
              <span className="rp-row-meta">
                {formatDayMonth(player.birthday)}
                {teamNames(player.teams) ? ` · ${teamNames(player.teams)}` : ''}
                {` · ${t('rpGamesPlayed', { count: player.games })}`}
              </span>
            </div>
          </Link>
        )) : <div className="rp-empty">{t('rpNoData')}</div>}
      </ReportBlock>

      <ReportBlock
        tabId="best"
        tabActive={mobileTab === 'best'}
        title={t('rpBest')}
        loading={statsLoading}
        error={statsError}
        filters={(
          <ReportDateRange
            from={statsDates.from}
            to={statsDates.to}
            onFrom={(from) => setStatsDates((prev) => ({ ...prev, from }))}
            onTo={(to) => setStatsDates((prev) => ({ ...prev, to }))}
            t={t}
            disabled={!champId}
          />
        )}
      >
        <div className="rp-section">
          <h3>{t('rpScorers')}</h3>
          {stats.scorers.length ? stats.scorers.map((item, index) => (
            <RankRow
              key={`scorer-${item.id}`}
              rank={index + 1}
              thumb={<PlayerThumb player={item} />}
              title={personName(item)}
              meta={teamNames(item.teams)}
              count={item.goals}
              to={playerCardPath(item.id)}
            />
          )) : <div className="rp-empty">{t('rpNoData')}</div>}
        </div>
        <div className="rp-section">
          <h3>{t('rpAssists')}</h3>
          {stats.assists.length ? stats.assists.map((item, index) => (
            <RankRow
              key={`assist-${item.id}`}
              rank={index + 1}
              thumb={<PlayerThumb player={item} />}
              title={personName(item)}
              meta={teamNames(item.teams)}
              count={item.assists}
              to={playerCardPath(item.id)}
            />
          )) : <div className="rp-empty">{t('rpNoData')}</div>}
        </div>
        <div className="rp-section">
          <h3>{t('rpGoalsAssists')}</h3>
          {stats.goals_assists.length ? stats.goals_assists.map((item, index) => (
            <RankRow
              key={`ga-${item.id}`}
              rank={index + 1}
              thumb={<PlayerThumb player={item} />}
              title={personName(item)}
              meta={`${teamNames(item.teams)}${item.goals != null ? ` · ${item.goals}+${item.assists}` : ''}`}
              count={item.goals_assists}
              to={playerCardPath(item.id)}
            />
          )) : <div className="rp-empty">{t('rpNoData')}</div>}
        </div>
        <div className="rp-section">
          <h3>{t('rpYellow')}</h3>
          {stats.yellow_cards.length ? stats.yellow_cards.map((item, index) => (
            <RankRow
              key={`yc-${item.id}`}
              rank={index + 1}
              thumb={<PlayerThumb player={item} />}
              title={personName(item)}
              meta={teamNames(item.teams)}
              count={item.yellow_cards}
              to={playerCardPath(item.id)}
            />
          )) : <div className="rp-empty">{t('rpNoData')}</div>}
        </div>
        <div className="rp-section">
          <h3>{t('rpRed')}</h3>
          {stats.red_cards.length ? stats.red_cards.map((item, index) => (
            <RankRow
              key={`rc-${item.id}`}
              rank={index + 1}
              thumb={<PlayerThumb player={item} />}
              title={personName(item)}
              meta={teamNames(item.teams)}
              count={item.red_cards}
              to={playerCardPath(item.id)}
            />
          )) : <div className="rp-empty">{t('rpNoData')}</div>}
        </div>
        <div className="rp-section">
          <h3>{t('rpTeamsGoals')}</h3>
          {stats.teams.length ? stats.teams.map((item, index) => (
            <RankRow
              key={`team-${item.id}`}
              rank={index + 1}
              thumb={<TeamThumb team={item} />}
              title={item.name}
              count={item.goals}
              to={`/teams_management/${item.id}`}
            />
          )) : <div className="rp-empty">{t('rpNoData')}</div>}
        </div>
        <div className="rp-section">
          <h3>{t('rpBiggestWins')}</h3>
          {stats.biggest_wins.length ? stats.biggest_wins.map((match) => (
            <MatchMini key={`win-${match.id}`} match={match} />
          )) : <div className="rp-empty">{t('rpNoData')}</div>}
        </div>
        <div className="rp-section">
          <h3>{t('rpRoughest')}</h3>
          {stats.roughest_matches.length ? stats.roughest_matches.map((match) => (
            <div key={`rough-${match.id}`}>
              <MatchMini match={match} />
              <div className="rp-sub">
                {t('rpYellow')}: {match.yellow_cards || 0} · {t('rpRed')}: {match.red_cards || 0}
              </div>
            </div>
          )) : <div className="rp-empty">{t('rpNoData')}</div>}
        </div>
      </ReportBlock>
    </ReportPage>
  );
}

export default PlayerStats;
